import CryptoJS from "crypto-js";

const MAIN_KEY = "Z3us3ncryPtEd.K3Y.Z3us3ncryPtEd+";
const KEY_256 = CryptoJS.SHA256(MAIN_KEY);
const FIELD = CryptoJS.SHA1("session").toString();

type Stored = { data: string };

const hasStorage = () => typeof window !== "undefined" && !!window.localStorage;

export async function encrypt<T>(value: T): Promise<Stored> {
  try {
    const iv = CryptoJS.lib.WordArray.random(16);
    const plaintext = CryptoJS.enc.Utf8.parse(JSON.stringify(value));
    const cipher = CryptoJS.AES.encrypt(plaintext, KEY_256, { iv });
    const payload = iv.clone().concat(cipher.ciphertext).toString(CryptoJS.enc.Base64);
    return { data: payload };
  } catch (e) {
    throw new Error("No se pudo cifrar la sesión.");
  }
}

export async function decrypt<T>(stored: Stored): Promise<T> {
  try {
    if (!stored?.data) throw new Error("Payload vacío");
    const raw = CryptoJS.enc.Base64.parse(stored.data); // iv|ciphertext

    if (!raw?.sigBytes || raw.sigBytes <= 16) {
      throw new Error("Payload inválido");
    }

    const iv = CryptoJS.lib.WordArray.create(raw.words.slice(0, 4), 16);
    const ciphertext = CryptoJS.lib.WordArray.create(raw.words.slice(4), raw.sigBytes - 16);

    const cipherParams = CryptoJS.lib.CipherParams.create({ ciphertext });
    const out = CryptoJS.AES.decrypt(cipherParams, KEY_256, { iv });
    const json = out.toString(CryptoJS.enc.Utf8);
    if (!json) throw new Error("No se pudo descifrar (clave/IV inválidos)");

    return JSON.parse(json) as T;
  } catch (e) {
    throw new Error("No se pudo descifrar la sesión.");
  }
}

export async function localStorageUserSession<T>(request: T): Promise<void> {
  try {
    if (!hasStorage()) return;
    const enc = await encrypt(request);
    window.localStorage.setItem(FIELD, JSON.stringify(enc));
  } catch (e) {
    // No lanzar: guardar sesión no debe tumbar la UI
  }
}

export function existLocalStorageUserSession(): boolean {
  try {
    if (!hasStorage()) return false;
    return !!window.localStorage.getItem(FIELD);
  } catch {
    return false;
  }
}

export async function getLocalStorageUserSession<T = unknown>(): Promise<T | null> {
  try {
    if (!hasStorage()) return null;
    const raw = window.localStorage.getItem(FIELD);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Stored;
    if (!parsed?.data) return null;

    return await decrypt<T>(parsed);
  } catch {
    try {
      window.localStorage.removeItem(FIELD);
    } catch {
      // swallow
    }
    return null;
  }
}

export function clearLocalStorageUserSession() {
  try {
    if (!hasStorage()) return;
    window.localStorage.removeItem(FIELD);
    try {
      window.sessionStorage.removeItem(FIELD);
    } catch {
      // swallow
    }
  } catch {
    // swallow
  }
}
